import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

import { ControlledTextarea, ControlledTextInput } from '@monorepo/components';

import { ApiResponseError } from '@/apis/http';
import { useContactUsForm } from '@/components/modules/Landing/api';
import ControlledPhoneInput from '@/components/shared/PhoneInput/ControlledPhoneInput';
import { IContactUsRequest } from '@/types';
import type { ContactUsTypes } from '@/types/entities/contact.entities';
import { cn } from '@/utils/helper';
import yup, { normalizePhoneNumber } from '@/utils/yup';

const contactUsSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Name is required')
    .max(50, 'Name must not exceed 50 characters'),
  email: yup
    .string()
    .trim()
    .required('Email is required')
    .email('Please enter a valid email address'),
  companyName: yup.string().trim().optional(),
  message: yup
    .string()
    .trim()
    .required('Message is required')
    .min(10, 'Please enter at least 10 characters so we can help you.'),
  phoneNumber: yup.string().phoneNumber({
    allowLocal: true,
    message: 'Please enter a valid phone number, or leave this field blank.',
  }),
});

export type ContactUsSchemaFormData = yup.InferType<typeof contactUsSchema>;

const contactUsFormDefaultValues: ContactUsSchemaFormData = {
  name: '',
  email: '',
  companyName: '',
  message: '',
  phoneNumber: '',
};

const FIELD_ORDER: Array<keyof ContactUsSchemaFormData> = [
  'name',
  'email',
  'phoneNumber',
  'companyName',
  'message',
];

const FIELD_LABELS: Record<keyof ContactUsSchemaFormData, string> = {
  name: 'Name',
  email: 'Email',
  phoneNumber: 'Phone number',
  companyName: 'Company name',
  message: 'Message',
};

const getFieldErrorMessage = (
  errors: FieldErrors<ContactUsSchemaFormData>,
  fieldName: keyof ContactUsSchemaFormData
) => {
  const fieldError = errors[fieldName] as { message?: unknown } | undefined;
  return typeof fieldError?.message === 'string' ? fieldError.message : null;
};

const getFormErrorSummary = (errors: FieldErrors<ContactUsSchemaFormData>) =>
  FIELD_ORDER.reduce<string[]>((messages, fieldName) => {
    const message = getFieldErrorMessage(errors, fieldName);
    if (message) {
      messages.push(`${FIELD_LABELS[fieldName]}: ${message}`);
    }
    return messages;
  }, []);

const getFirstInvalidField = (errors: FieldErrors<ContactUsSchemaFormData>) =>
  FIELD_ORDER.find((fieldName) => getFieldErrorMessage(errors, fieldName));

const getErrorMessages = (error: unknown): string[] => {
  if (error instanceof ApiResponseError) {
    const apiErrorMessages = error.data?.message;
    if (Array.isArray(apiErrorMessages)) {
      return apiErrorMessages.filter(
        (message): message is string => typeof message === 'string' && message.trim().length > 0
      );
    }
    if (typeof apiErrorMessages === 'string' && apiErrorMessages.trim().length > 0) {
      return [apiErrorMessages];
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return [error.message];
  }

  return [];
};

const mapServerMessageToField = (message: string): keyof ContactUsSchemaFormData | null => {
  const lowercaseMessage = message.toLowerCase();

  if (lowercaseMessage.includes('phone') || lowercaseMessage.includes('country code')) {
    return 'phoneNumber';
  }

  if (lowercaseMessage.includes('company')) {
    return 'companyName';
  }

  if (lowercaseMessage.includes('email')) {
    return 'email';
  }

  if (lowercaseMessage.includes('message') || lowercaseMessage.includes('at least 10 characters')) {
    return 'message';
  }

  if (lowercaseMessage.includes('name')) {
    return 'name';
  }

  return null;
};

const getFriendlyFieldMessage = (
  fieldName: keyof ContactUsSchemaFormData,
  serverMessage: string
) => {
  const lowercaseMessage = serverMessage.toLowerCase();

  if (fieldName === 'message' && lowercaseMessage.includes('at least 10')) {
    return 'Please enter at least 10 characters so we can help you.';
  }

  if (fieldName === 'phoneNumber') {
    return 'Please enter a valid phone number, or leave this field blank.';
  }

  return serverMessage;
};

/** What the form reports to its host: an unsent edit, and a submission in flight. */
export interface ContactUsFormEditState {
  dirty: boolean;
  pending: boolean;
}

/** How the form behaves; independent of where and whether its fields are shown. */
export interface ContactUsFormOptions {
  /** Sent as the request's `type`. The caller always chooses it (CONTACT on the marketing page, SUPPORT in Atlas). */
  type: ContactUsTypes;
  /**
   * Starting values (for example the signed-in person's name and email). Read once, when the form mounts; a value
   * arriving later never resets the form. A successful send resets back to these values. Remount (key) to change them.
   */
  initialValues?: Partial<ContactUsSchemaFormData>;
  /**
   * An unsent edit recovered by the host (for example after leaving the page). Applied once, when the form mounts, on
   * top of `initialValues`, and counted as unsent: the form reports it as dirty.
   */
  restoredValues?: Partial<ContactUsSchemaFormData>;
  /** Reported whenever the form gains or loses an unsent edit or a submission starts or ends. */
  onEditStateChange?: (state: ContactUsFormEditState) => void;
  /** Every change of the field values, with whether they differ from the starting values. */
  onValuesChange?: (values: ContactUsSchemaFormData, dirty: boolean) => void;
  /** Each change of this value (after the first render) resets the form to its starting values: a discarded edit. */
  discardKey?: number;
  /**
   * Called when a submission starts; returns a check the form runs once the request settles. When the check fails
   * (the host closed the form, the account or scope changed) the result is dropped: no reset, no success message, no
   * error notification and no focus move. Without it, results always apply, as on the marketing page.
   */
  beginSubmission?: () => () => boolean;
  /** Called after a successful send whose submission is still current. */
  onSubmitted?: () => void;
}

export interface ContactUsFormProps extends ContactUsFormOptions {
  isDarkMode?: boolean;
  /** `stacked` keeps one column at every width (a narrow side pane); `responsive` is the marketing page's grid. */
  layout?: 'responsive' | 'stacked';
}

const sameValues = (a: ContactUsSchemaFormData, b: ContactUsSchemaFormData) =>
  FIELD_ORDER.every((field) => (a[field] ?? '') === (b[field] ?? ''));

/**
 * The contact form's state and submission. It lives with the caller, so a caller that hides the fields (the marketing
 * page's `isFormVisible`) keeps the values, the verification token and the success state while they are hidden.
 */
export function useContactUsFormController({
  type,
  initialValues,
  restoredValues,
  onEditStateChange,
  onValuesChange,
  discardKey,
  beginSubmission,
  onSubmitted,
}: ContactUsFormOptions) {
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  // One submission at a time: a second click or Enter while one is validating or in flight sends nothing.
  const submittingRef = useRef(false);

  const [defaultValues] = useState<ContactUsSchemaFormData>(() => ({
    ...contactUsFormDefaultValues,
    ...initialValues,
  }));
  const contactUsFormHandler = useContactUsForm({ suppressErrorToast: true });
  const form = useForm<ContactUsSchemaFormData>({
    resolver: yupResolver(contactUsSchema),
    defaultValues,
  });

  // Subscribed before the recovered draft is applied below, so the host also hears about the restored values.
  const valuesRef = useRef(onValuesChange);
  valuesRef.current = onValuesChange;
  useEffect(() => {
    const subscription = form.watch((values) => {
      const current = { ...contactUsFormDefaultValues, ...values } as ContactUsSchemaFormData;
      valuesRef.current?.(current, !sameValues(current, defaultValues));
    });
    return () => subscription.unsubscribe();
  }, [form, defaultValues]);

  // A recovered draft is applied once, as edits (dirty), never as the starting values.
  const restoredRef = useRef(restoredValues);
  useEffect(() => {
    const restored = restoredRef.current;
    restoredRef.current = undefined;
    if (!restored) return;
    for (const field of FIELD_ORDER) {
      const value = restored[field];
      if (typeof value === 'string' && value !== defaultValues[field]) {
        form.setValue(field, value, { shouldDirty: true });
      }
    }
  }, [form, defaultValues]);

  // The host discarded the message: back to the starting values, so the form and the host agree it is clean even if
  // the change Discard released does not complete (another departure check cancels it).
  const discardedRef = useRef(discardKey);
  useEffect(() => {
    if (discardedRef.current === discardKey) return;
    discardedRef.current = discardKey;
    form.reset(defaultValues);
  }, [discardKey, form, defaultValues]);

  const formErrorSummary = useMemo(
    () => getFormErrorSummary(form.formState.errors),
    [form.formState.errors]
  );

  const { isDirty } = form.formState;
  const isPending = contactUsFormHandler.isPending;
  const reportRef = useRef(onEditStateChange);
  reportRef.current = onEditStateChange;
  useEffect(() => {
    reportRef.current?.({ dirty: isDirty, pending: isPending });
  }, [isDirty, isPending]);

  const focusAndScrollToField = (fieldName: keyof ContactUsSchemaFormData) => {
    form.setFocus(fieldName);

    if (typeof document !== 'undefined') {
      const fieldElement = document.querySelector<HTMLElement>(`[name="${String(fieldName)}"]`);
      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const focusAndScrollToFirstInvalidField = (errors: FieldErrors<ContactUsSchemaFormData>) => {
    const firstInvalidField = getFirstInvalidField(errors);
    if (!firstInvalidField) {
      return;
    }

    focusAndScrollToField(firstInvalidField);
  };

  const handleSubmit = async (data: ContactUsSchemaFormData) => {
    if (submittingRef.current) {
      return;
    }
    setIsSubmittedSuccessfully(false);
    form.clearErrors();

    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) {
      showNotification({
        message: 'Please complete the verification check.',
        color: 'red',
      });
      return;
    }

    const normalizedPhone = data.phoneNumber
      ? normalizePhoneNumber(data.phoneNumber, { allowLocal: true })
      : null;

    if (data.phoneNumber && !normalizedPhone) {
      const phoneFieldError = 'Please enter a valid phone number, or leave this field blank.';
      form.setError('phoneNumber', {
        type: 'manual',
        message: phoneFieldError,
      });
      focusAndScrollToField('phoneNumber');
      return;
    }

    const payload: IContactUsRequest = {
      name: data.name,
      email: data.email,
      message: data.message,
      ...(normalizedPhone ? { phone: normalizedPhone } : {}),
      ...(data.companyName?.trim() ? { companyName: data.companyName.trim() } : {}),
      type,
      ...(turnstileToken ? { turnstileToken } : {}),
    };
    const isCurrent = beginSubmission?.() ?? (() => true);
    // What was sent. Edits made while it was in flight were not sent, so a success never clears them.
    const sent = form.getValues();
    submittingRef.current = true;
    try {
      await contactUsFormHandler.mutateAsync(payload);
      submittingRef.current = false;
      if (!isCurrent()) {
        return;
      }
      setIsSubmittedSuccessfully(true);
      if (sameValues(form.getValues(), sent)) {
        form.reset(defaultValues);
      }
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      onSubmitted?.();
    } catch (error) {
      submittingRef.current = false;
      if (!isCurrent()) {
        return;
      }
      setIsSubmittedSuccessfully(false);

      const errorMessages = getErrorMessages(error);
      const serverFieldErrors = new Set<keyof ContactUsSchemaFormData>();

      errorMessages.forEach((errorMessage) => {
        const fieldName = mapServerMessageToField(errorMessage);
        if (!fieldName || serverFieldErrors.has(fieldName)) {
          return;
        }

        form.setError(fieldName, {
          type: 'server',
          message: getFriendlyFieldMessage(fieldName, errorMessage),
        });
        serverFieldErrors.add(fieldName);
      });

      const firstFieldWithError = FIELD_ORDER.find((fieldName) => serverFieldErrors.has(fieldName));

      if (firstFieldWithError) {
        focusAndScrollToField(firstFieldWithError);
        return;
      }

      showNotification({
        message: errorMessages[0] || 'Unable to submit form right now. Please try again.',
        color: 'red',
      });
    }
  };

  const handleInvalidSubmit = (errors: FieldErrors<ContactUsSchemaFormData>) => {
    setIsSubmittedSuccessfully(false);
    focusAndScrollToFirstInvalidField(errors);
  };

  return {
    form,
    formErrorSummary,
    isSubmittedSuccessfully,
    turnstileToken,
    setTurnstileToken,
    turnstileRef,
    contactUsFormHandler,
    handleSubmit,
    handleInvalidSubmit,
  };
}

export type ContactUsFormController = ReturnType<typeof useContactUsFormController>;

/** The form's fields, summary, verification widget and Send button, for a controller the caller owns. */
export function ContactUsFormFields({
  controller,
  isDarkMode = false,
  layout = 'responsive',
}: {
  controller: ContactUsFormController;
  isDarkMode?: boolean;
  layout?: 'responsive' | 'stacked';
}) {
  const {
    form,
    formErrorSummary,
    isSubmittedSuccessfully,
    turnstileToken,
    setTurnstileToken,
    turnstileRef,
    contactUsFormHandler,
    handleSubmit,
    handleInvalidSubmit,
  } = controller;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
        className={cn(
          // Stacked is a flex column, not a one-track grid: the fields' sm:col-span-2 would add an implicit second track.
          layout === 'responsive'
            ? 'grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2'
            : 'flex flex-col gap-y-5',
          {
            'text-white': isDarkMode,
            'text-gray-900': !isDarkMode,
          }
        )}>
        <p className={cn('text-xs sm:col-span-2', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
          Required fields are marked with *
        </p>
        {form.formState.submitCount > 0 && formErrorSummary.length > 0 && (
          <div
            className={cn('rounded-xl border px-4 py-3 sm:col-span-2', {
              'border-red-500/40 bg-red-500/10 text-red-100': isDarkMode,
              'border-red-200 bg-red-50 text-red-800': !isDarkMode,
            })}
            aria-live="polite">
            <p className="text-sm font-semibold">Please fix the following:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {formErrorSummary.map((errorMessage, index) => (
                <li key={`${errorMessage}-${index}`}>{errorMessage}</li>
              ))}
            </ul>
          </div>
        )}
        {isSubmittedSuccessfully && (
          <div
            className={cn('rounded-xl border px-4 py-3 sm:col-span-2', {
              'border-emerald-500/40 bg-emerald-500/10 text-emerald-100': isDarkMode,
              'border-emerald-200 bg-emerald-50 text-emerald-800': !isDarkMode,
            })}
            aria-live="polite">
            <p className="text-sm font-semibold">
              Thanks, we received your message. We&rsquo;ll reply by email.
            </p>
          </div>
        )}
        <ControlledTextInput
          name="name"
          placeholder="Jane Smith"
          label="Name"
          withAsterisk
          control={form.control}
          classNames={{
            root: 'col-span-1',
            label: cn('font-medium text-sm', isDarkMode ? 'text-gray-200' : ''),
            input: cn(
              'h-11 rounded-xl',
              isDarkMode
                ? 'bg-[#020617] border-gray-700 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-gray-50/60 focus:bg-white'
            ),
          }}
        />
        <ControlledTextInput
          name="email"
          withAsterisk
          placeholder="name@email.com"
          label="Email"
          control={form.control}
          classNames={{
            root: 'col-span-1',
            label: cn('font-medium text-sm', isDarkMode ? 'text-gray-200' : ''),
            input: cn(
              'h-11 rounded-xl',
              isDarkMode
                ? 'bg-[#020617] border-gray-700 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-gray-50/60 focus:bg-white'
            ),
          }}
        />
        <ControlledPhoneInput
          name="phoneNumber"
          placeholder="5551234567"
          className="col-span-1"
          label="Phone Number"
          control={form.control}
          description="Optional. You can enter a local number (example: 5551234567)."
          classNames={{
            root: 'col-span-1',
            label: cn('font-medium text-sm', isDarkMode ? 'text-gray-200' : ''),
            description: cn('text-xs mt-1', isDarkMode ? 'text-gray-400' : 'text-gray-500'),
            input: cn(
              'disabled:cursor-default h-11 rounded-xl',
              isDarkMode
                ? 'bg-[#020617] border-gray-700 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-gray-50/60 focus:bg-white'
            ),
            error: 'text-red-500 text-xs mt-1',
          }}
          error={form.formState.errors.phoneNumber?.message}
          maxLength={20}
        />
        <ControlledTextInput
          name="companyName"
          placeholder="Acme Inc. (optional)"
          label="Company Name"
          control={form.control}
          classNames={{
            root: 'col-span-1',
            label: cn('font-medium text-sm', isDarkMode ? 'text-gray-200' : ''),
            input: cn(
              'h-11 rounded-xl',
              isDarkMode
                ? 'bg-[#020617] border-gray-700 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-gray-50/60 focus:bg-white'
            ),
          }}
        />
        <div className="sm:col-span-2">
          <ControlledTextarea
            name="message"
            label="Message"
            placeholder="How can we help?"
            description="Please share a little detail (at least 10 characters)."
            autosize
            resize="vertical"
            withAsterisk
            minRows={5}
            control={form.control}
            classNames={{
              input: cn(
                'rounded-xl',
                isDarkMode
                  ? 'bg-[#020617] border-gray-700 text-gray-100 placeholder:text-gray-500'
                  : 'border-gray-200 bg-gray-50/60 focus:bg-white'
              ),
              description: cn('text-xs mt-1', isDarkMode ? 'text-gray-400' : 'text-gray-500'),
              label: cn('text-sm font-medium mb-2', isDarkMode ? 'text-gray-200' : ''),
            }}
          />
        </div>
        {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
          <div className="sm:col-span-2">
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken(null)}
              onExpire={() => setTurnstileToken(null)}
              options={{
                theme: isDarkMode ? 'dark' : 'light',
                size: 'normal',
              }}
            />
          </div>
        )}
        <Button
          size="lg"
          className={cn('w-full rounded-xl text-sm font-semibold tracking-wide sm:col-span-2', {
            'bg-[#d4a843] text-[#0d1b3e] hover:bg-[#c49a3a]': !isDarkMode,
            'bg-primary-600 text-white hover:bg-primary-700': isDarkMode,
          })}
          loading={contactUsFormHandler.isPending}
          disabled={
            contactUsFormHandler.isPending ||
            (!!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken)
          }
          type="submit">
          {contactUsFormHandler.isPending ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </FormProvider>
  );
}

/** The contact form with its own state, for a caller that always shows it (the Atlas Support pane). */
const ContactUsForm: React.FC<ContactUsFormProps> = ({ isDarkMode, layout, ...options }) => {
  const controller = useContactUsFormController(options);
  return <ContactUsFormFields controller={controller} isDarkMode={isDarkMode} layout={layout} />;
};

export default ContactUsForm;
